import { useNavigate, useParams } from "react-router-dom";
import {
    BundleTypes,
    DiscountTypes,
    nonEmptyOverride,
    RedirectOptionsTypes,
    updateBundleConfigWithRealTimeProducts,
    validateFontSize,
    validateHexalColor,
    WidgetPositionsTypes,
    WidgetShowOptionsTypes,
} from "../constants";
import FormPreviewWrapper from "../../../components/FormPreviewWrapper/FormPreviewWrapper";
import FBTDesignForm from "./FBTDesignForm";
import FBTSettingsForm from "./FBTSettingsForm";
import FBTTargetForm from "./FBTTargetForm";
import useFBTHook from "./useFBTHook";
import styles from "./FBT.module.css";
import { endPoints, getData } from "../../../utilities/networkCalls";
import { createContext, Dispatch, useContext, useEffect, useRef, useState } from "react";
import { getBundleRoute, MAX_LENGTH_DESKTOP, MAX_LENGTH_MOBILE } from "../../constants";
import { FormError, useField, useForm } from "@shopify/react-form";
import { FBTPreview } from "./FBTBundle/FBTBundle";
import { Action, ChangeEvents, FBTConfigInterface, VolumeConfigInterface } from "../Types";
import { AppConfigContext } from "../../../context/AppConfigContext";
import { getUpdatedTargeting, validateTargeting } from "../../shouldRenderTargeting";
import useBundleMixpanelHook from "../../../hooks/useBundleMixpanelHook";
import FBTContentForm from "./FBTContentForm";
import { scrollToTop } from "../../../utilities/functions";
import { useInspectorMode } from "../../../hooks/useInspectorMode";
import TemplateSelectionScreen from "../../../components/TemplateSelectionScreen/TemplateSelectionScreen";

let FBT_TYPE: null | "all" | "manual" = null;
let FBT_MANUAL_SCOPE: null | "all" | "specific" = null;
export interface FBTBundleContextType {
    bundleConfig?: FBTConfigInterface;
    dispatchBundleConfig: Dispatch<Action<FBTConfigInterface>>;
    handleNext: (step: number, stepName: string) => void;
    fields: any;
    submit: (event?: React.FormEvent) => Promise<void>;
    reset: () => void;
    validate: () => FormError[];
    makeClean: () => void;
    isMobile: boolean;
    SwatchesBundleConfigTouchedRef: React.MutableRefObject<Record<string, string>>;
    // --- inspector mode additions ---
    inspectorMode?: boolean;
    setInspectorMode?: Dispatch<React.SetStateAction<boolean>>;
    inspectorTarget: any | null;
}

export interface BundleAsProductType {
    product_title: string;
    product_description: string;
    images: ImageType[]; // Use the defined ImageType here
}

export interface ImageType {
    originalSource: string; // URL of the image
    alt: string; // Alt text for the image
    mediaContentType: "IMAGE"; // To specify the type
}

export const FBTBundleContext = createContext<FBTBundleContextType>(null as any);

const FBT = () => {
    const { bundleConfig, dispatchBundleConfig } = useFBTHook();

    const [_config, setConfig] = useState({
        ...bundleConfig,
    });
    const { trackFromTabs } = useBundleMixpanelHook();
    const SwatchesBundleConfigTouchedRef = useRef<Record<string, string>>({});
    const isProductPageVisibleRef = useRef(bundleConfig.isProductPageVisible);
    isProductPageVisibleRef.current = bundleConfig.isProductPageVisible;

    const {
        fields,
        submit,
        // submitting,
        dirty,
        reset,
        submitErrors,
        makeClean,
        validate,
    } = useForm({
        fields: {
            isProductPageVisible: useField(_config?.isProductPageVisible),
            allowed_product_pages: useField(_config?.allowed_product_pages),
            render_product_pages_type: useField(_config?.render_product_pages_type),
            hide_native_elements: useField(_config?.hide_native_elements || ""),
            bundle_as_product: {
                product_title: useField({
                    value: _config?.bundle_as_product?.product_title || "",
                    validates: (val) => {
                        if (isProductPageVisibleRef.current) {
                            if (!val) {
                                return "Bundle Product Title is required";
                            }
                            if (val.length < 3) {
                                return "Bundle Product Title must be at least 3 characters long";
                            }
                        }
                        return undefined;
                    },
                }),
                product_description: useField(_config?.bundle_as_product?.product_description || ""),
                images: useField(_config?.bundle_as_product?.images || []),
                show_on_product_page: useField(_config?.bundle_as_product?.show_on_product_page || []),
                product_handle: useField(_config?.bundle_as_product?.product_handle || ""),
                double_discount: useField(_config?.bundle_as_product?.double_discount || false),
                collections: useField(_config?.bundle_as_product?.collections || []),
            },

            title: useField({
                value: _config?.title,
                validates: [(val) => nonEmptyOverride(val, "Title is required")],
            }),
            offer_name: useField(_config?.offer_name),
            discount_label_template: useField({
                value: _config?.discount_label_template,
                validates: [(val) => nonEmptyOverride(val, "Discount label is required")],
            }),
            description: useField(_config?.description),
            bundle_product_level: useField<"product" | "variant">(_config?.bundle_product_level),
            fbt_product_selection: useField<"all" | "manual">(_config?.fbt_product_selection),
            fbt_show_checkboxes: useField<0 | 1>(_config?.fbt_show_checkboxes),
            ai_fbt_product_limit: useField<number>(_config?.ai_fbt_product_limit),
            show_ai_fbt_for: useField<"every" | "include" | "exclude">(_config?.show_ai_fbt_for),
            ai_fbt_allowed_products: useField<string[]>(_config?.ai_fbt_allowed_products),
            // NEW: manual product scope field
            manual_product_scope: useField<"all" | "specific">(_config?.manual_product_scope || "specific"),
            products: useField({
                value: _config?.products,
                validates: [
                    (products) => {
                        // Skip validation when manual mode uses "all products" scope
                        if (FBT_TYPE == "manual" && FBT_MANUAL_SCOPE !== "all") {
                            if (products.length <= 0) {
                                return "Products are required";
                            }
                            for (const p of products) {
                                if (p.quantity < 0) {
                                    return "Product quantity cannot be negative";
                                }
                                if (p.quantity === 0) {
                                    return "Product quantity cannot be zero";
                                }
                            }
                        }
                    },
                ],
            }),
            offered_products: useField({
                value: _config?.offered_products,
                validates: [
                    (products) => {
                        // Skip validation when manual mode uses "all products" scope
                        if (FBT_TYPE == "manual" && FBT_MANUAL_SCOPE !== "all") {
                            if (products.length <= 0) {
                                return "Suggested Products are required";
                            }
                            for (const p of products) {
                                if (p.quantity < 0) {
                                    return "Suggested Product quantity cannot be negative";
                                }
                                if (p.quantity === 0) {
                                    return "Suggested Product quantity cannot be zero";
                                }
                            }
                        }
                    },
                ],
            }),
            discount_type: useField<DiscountTypes>({
                value: _config?.discount_type,
                validates: [(val) => nonEmptyOverride(val, "Type is required")],
            }),
            discount_value: useField({
                value: _config?.discount_value as unknown as string,
                validates: [(val) => nonEmptyOverride(val, "Discount value is required")],
            }),
            order_discounts: useField(_config?.order_discounts),
            product_discounts: useField(_config?.product_discounts),
            shipping_discounts: useField(_config?.shipping_discounts),
            add_to_cart_cta_text: useField({
                value: _config?.add_to_cart_cta_text,
                validates: [(val) => nonEmptyOverride(val, "CTA value is required")],
            }),
            footer_total_text: useField({
                value: _config?.footer_total_text,
                validates: [(val) => nonEmptyOverride(val, "Footer total text is required")],
            }),
            footer_text: useField({
                value: _config?.footer_text,
                validates: [(val) => nonEmptyOverride(val, "Footer text is required")],
            }),
            free_text: useField({
                value: _config?.free_text,
                validates: [(val) => nonEmptyOverride(val, "Free text is required")],
            }),

            start_date: useField(_config?.start_date),
            end_date: useField(_config?.end_date || null),
            use_compare_at_price: useField(_config?.use_compare_at_price || false),
            apply_discount_on_compare_price: useField(_config?.apply_discount_on_compare_price || false),
            prevent_deselect_main_product: useField(_config?.prevent_deselect_main_product),
            no_preselect_offered_products: useField(_config?.no_preselect_offered_products),
            show_swatches: useField(_config?.show_swatches),
            selected_template_fbt: useField(_config?.selected_template_fbt),
            swatchesProductsMap: useField({
                value: _config?.swatchesProductsMap,
                validates: [],
                dirtyStateComparator: (v1, v2) => {
                    const res = JSON.stringify(v1) != JSON.stringify(v2);
                    return res;
                },
            }),
            swatchOptions: useField({
                value: _config?.swatchOptions,
                validates: [],
                dirtyStateComparator: (v1, v2) => {
                    return JSON.stringify(v1) != JSON.stringify(v2);
                },
            }),
            swatchShape: useField(_config?.swatchShape),
            swatchSize: useField(_config?.swatchSize),
            new_bundle_position: useField(_config?.new_bundle_position),
            new_custom_selector: useField(_config?.new_custom_selector),

            bundles_design: {
                default_cta: useField<boolean>(_config?.bundles_design?.default_cta),
                bundle_title_color: useField({
                    value: _config?.bundles_design?.bundle_title_color,
                    validates: [(val) => validateHexalColor(val, "Bundle title")],
                }),
                swatches_select_color: useField({
                    value: _config?.bundles_design?.swatches_select_color,
                    validates: [(val) => validateHexalColor(val, "Swatches")],
                }),
                bundle_title_style: useField(_config?.bundles_design?.bundle_title_style),
                bundle_title_size: useField({
                    value: _config?.bundles_design?.bundle_title_size as unknown as number,
                    validates: [(val) => validateFontSize(val, "Bundle title font size")],
                }),
                bundle_widget_bg: useField({
                    value: _config?.bundles_design?.bundle_widget_bg as unknown as string,
                    validates: [(val) => validateHexalColor(val, "Bundle widget")],
                }),
                bundle_widget_font_family: useField(_config?.bundles_design?.bundle_widget_font_family),
                product_background_color: useField({
                    value: _config?.bundles_design?.product_background_color,
                    validates: [(val) => validateHexalColor(val, "Product background")],
                }),
                product_border_color: useField({
                    value: _config?.bundles_design?.product_border_color,
                    validates: [(val) => validateHexalColor(val, "Product border")],
                }),
                product_name_color: useField({
                    value: _config?.bundles_design?.product_name_color,
                    validates: [(val) => validateHexalColor(val, "Product name")],
                }),
                product_name_style: useField(_config?.bundles_design?.product_name_style),
                product_name_size: useField({
                    value: _config?.bundles_design?.product_name_size as unknown as string,
                    validates: [(val) => validateFontSize(val, "Product name font")],
                }),
                product_price_color: useField({
                    value: _config?.bundles_design?.product_price_color,
                    validates: [(val) => validateHexalColor(val, "Product price")],
                }),
                product_price_style: useField(_config?.bundles_design?.product_price_style),
                product_price_size: useField({
                    value: _config?.bundles_design?.product_price_size as unknown as string,
                    validates: [(val) => validateFontSize(val, "Product price font")],
                }),
                variant_selector_text_size: useField({
                    value: (_config?.bundles_design?.variant_selector_text_size as unknown as string) ?? 12,
                    validates: [(val) => validateFontSize(val, "Variant Selector size")],
                }),
                variant_selector_text_color: useField({
                    value: _config?.bundles_design?.variant_selector_text_color ?? "#383838",
                    validates: [(val) => validateHexalColor(val, "Variant Selector")],
                }),
                variant_selector_text_style: useField(
                    _config?.bundles_design?.variant_selector_text_style ?? "450 normal",
                ),
                product_img_size: useField(_config?.bundles_design?.product_img_size),
                product_img_show: useField(_config?.bundles_design?.product_img_show),
                product_img_border_radius: useField(_config?.bundles_design?.product_img_border_radius),

                border_radius: useField(_config?.bundles_design?.border_radius as unknown as string),
                border_color: useField({
                    value: _config?.bundles_design?.border_color,
                    validates: [(val) => validateHexalColor(val, "Product border")],
                }),
                border_width: useField({
                    value: _config?.bundles_design?.border_width as unknown as string,
                    validates: [
                        (val) =>
                            validateFontSize(val, "Border width font", {
                                min: 0,
                                max: 15,
                            }),
                    ],
                }),
                background_color: useField({
                    value: _config?.bundles_design?.background_color,
                    validates: [(val) => validateHexalColor(val, "Product background")],
                }),
                highlighted_offer_tag_color: useField({
                    value: _config?.bundles_design?.highlighted_offer_tag_color,
                    validates: [(val) => validateHexalColor(val, "Highlighted offer tag")],
                }),
                highlighted_offer_tag_style: useField(_config?.bundles_design?.highlighted_offer_tag_style),
                highlighted_offer_tag_size: useField({
                    value: _config?.bundles_design?.highlighted_offer_tag_size as unknown as string,
                    validates: [(val) => validateFontSize(val, "Offer tag font")],
                }),
                highlighted_price_tag_color: useField({
                    value: _config?.bundles_design?.highlighted_price_tag_color,
                    validates: [(val) => validateHexalColor(val, "Highlighted price tag")],
                }),
                highlighted_price_tag_style: useField(_config?.bundles_design?.highlighted_price_tag_style),
                highlighted_price_tag_size: useField({
                    value: _config?.bundles_design?.highlighted_price_tag_size as unknown as string,
                    validates: [(val) => validateFontSize(val, "Price tag font")],
                }),
                total_footer_text_color: useField({
                    value: _config?.bundles_design?.total_footer_text_color,
                    validates: [(val) => validateHexalColor(val, "Total footer text")],
                }),
                total_footer_text_style: useField(_config?.bundles_design?.total_footer_text_style),
                total_footer_text_size: useField({
                    value: _config?.bundles_design?.total_footer_text_size as unknown as string,
                    validates: [(val) => validateFontSize(val, "Total footer text font")],
                }),
                total_footer_price_text_color: useField({
                    value: _config?.bundles_design?.total_footer_price_text_color,
                    validates: [(val) => validateHexalColor(val, "Total footer price text")],
                }),
                total_footer_price_text_style: useField(_config?.bundles_design?.total_footer_price_text_style),
                total_footer_price_text_size: useField({
                    value: _config?.bundles_design?.total_footer_price_text_size as unknown as string,
                    validates: [(val) => validateFontSize(val, "Total footer price font")],
                }),
                total_footer_background_color: useField({
                    value: _config?.bundles_design?.total_footer_background_color,
                    validates: [(val) => validateHexalColor(val, "Total footer background")],
                }),
                total_footer_background_style: useField(_config?.bundles_design?.total_footer_background_style),
                add_to_cart_bg_color: useField({
                    value: _config?.bundles_design?.add_to_cart_bg_color,
                    validates: [(val) => validateHexalColor(val, "Add to cart background")],
                }),
                add_to_cart_border_radius: useField(
                    _config?.bundles_design?.add_to_cart_border_radius as unknown as string,
                ),
                add_to_cart_border_color: useField({
                    value: _config?.bundles_design?.add_to_cart_border_color,
                    validates: [(val) => validateHexalColor(val, "Add to cart border")],
                }),
                add_to_cart_border_width: useField(
                    _config?.bundles_design?.add_to_cart_border_width as unknown as string,
                ),
                add_to_cart_text_color: useField({
                    value: _config?.bundles_design?.add_to_cart_text_color,
                    validates: [(val) => validateHexalColor(val, "Add to cart text")],
                }),
                add_to_cart_text_size: useField({
                    value: _config?.bundles_design?.add_to_cart_text_size as unknown as string,
                    validates: [(val) => validateFontSize(val, "Add to cart text font")],
                }),
                add_to_cart_text_style: useField(_config?.bundles_design?.add_to_cart_text_style),
                product_img_border_width: useField({
                    value: _config?.bundles_design?.product_img_border_width,
                    validates: [(val) => validateFontSize(val, "Product image border width", { min: 0, max: 10 })],
                }),
                product_img_border_color: useField({
                    value: _config?.bundles_design?.product_img_border_color,
                    validates: [(val) => validateHexalColor(val, "Product image border")],
                }),
            },
            general_settings: {
                show_bundle_on_product_page: useField(_config?.general_settings?.show_bundle_on_product_page),
                show_multiple_bundles_on_product_page: useField(
                    _config?.general_settings?.show_multiple_bundles_on_product_page,
                ),
                redirect_after_adding_product_to_cart: useField<RedirectOptionsTypes>(
                    _config?.general_settings?.redirect_after_adding_product_to_cart || "",
                ),
                redirect_to_checkout: useField<boolean | undefined>(_config?.general_settings?.redirect_to_checkout),
                show_powered_by_kefi: useField<boolean | undefined>(_config?.general_settings?.show_powered_by_kefi),
                max_width: useField(_config?.general_settings?.max_width),
                override_max_width: useField(_config?.general_settings?.override_max_width),
                margin_top: useField(_config?.general_settings?.margin_top || 20),
                override_margin_top: useField(_config?.general_settings?.override_margin_top || false),
            },
            bundles_settings: {
                enable_stock_control: useField(_config?.bundles_settings?.enable_stock_control),
                out_of_stock_text: useField({
                    value: _config?.bundles_settings?.out_of_stock_text as string,
                    validates: [(val) => nonEmptyOverride(val, "Text is required")],
                }),
                listing_variant_with_image: useField(_config?.bundles_settings?.listing_variant_with_image),
                work_only_with_widget: useField(_config?.bundles_settings?.work_only_with_widget),
                bundle_position: useField<WidgetPositionsTypes>(
                    _config?.bundles_settings?.bundle_position as WidgetPositionsTypes,
                ),
                custom_selector: useField<string>(_config?.bundles_settings?.custom_selector || ""),
            },

            show_bundle_widget: useField<WidgetShowOptionsTypes>(_config?.show_bundle_widget),
            free_shipping: useField(_config?.free_shipping),
            animation_add_to_cart_btn: useField(_config?.animation_add_to_cart_btn),

            targeting: useField({
                value: _config.targeting,
                validates: [validateTargeting],
                dirtyStateComparator: (v1, v2) => {
                    return JSON.stringify(v1) != JSON.stringify(v2);
                },
            }),
        },
        onSubmit: async () => {
            return { status: "success" };
        },
    });

    const [selected, setSelected] = useState(0);
    const [showBundlerLevel, setShowBundlerLevel] = useState(false);
    const [fetchingBundle, setFetchingBundle] = useState(true);
    const [isMobile, setIsMobile] = useState(false);
    const maxLength = isMobile ? MAX_LENGTH_MOBILE : MAX_LENGTH_DESKTOP;
    const { bId } = useParams();
    const goTo = useNavigate();
    const appConfigContext = useContext(AppConfigContext);
    const { sft, globalSettingsInfo, themeColorsInfo } = appConfigContext;
    const { globalSettings, loading } = globalSettingsInfo;
    const { mergeDesignWithThemeColors } = themeColorsInfo;

    const userSaved = globalSettingsInfo?.globalSettings?.design_settings_fbt?.user_saved;

    console._log("bundle id", bId, setIsMobile, maxLength);
    // Update both module-level vars so validators always have latest values
    FBT_TYPE = fields?.fbt_product_selection.value || null;
    FBT_MANUAL_SCOPE = fields?.manual_product_scope?.value || null;
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => {
            window.removeEventListener("resize", checkMobile);
        };
    }, []);

    useEffect(() => {
        if (!loading) {
            if (bId) {
                fetchBundle(bId);
            } else {
                setFetchingBundle(false);

                const mergedDesignSettings = mergeDesignWithThemeColors(globalSettings?.design_settings_fbt);

                dispatchBundleConfig({
                    type: ChangeEvents.initState,
                    // @ts-expect-error hack
                    init: {
                        bundles_design: {
                            ...bundleConfig?.bundles_design,
                            ...(userSaved ? globalSettings?.design_settings_fbt : mergedDesignSettings),
                        } as VolumeConfigInterface["bundles_design"],
                        general_settings: {
                            ...bundleConfig?.general_settings,
                            // eslint-disable-next-line no-prototype-builtins
                            ...(globalSettings?.hasOwnProperty("general_settings")
                                ? {
                                      ...globalSettings?.general_settings,
                                      show_powered_by_kefi: globalSettings?.general_settings?.hasOwnProperty(
                                          "show_powered_by_kefi",
                                      )
                                          ? globalSettings?.general_settings?.show_powered_by_kefi
                                          : true,
                                  }
                                : {}),
                        },
                        bundles_settings: {
                            ...bundleConfig?.bundles_settings,
                            ...globalSettings?.bundle_settings_fbt,
                        } as VolumeConfigInterface["bundles_settings"],
                    },
                });
                setConfig((ps: any) => ({
                    ...bundleConfig,
                    bundles_design: {
                        ...ps.bundles_design,
                        ...(userSaved ? globalSettings?.design_settings_fbt : mergedDesignSettings),
                    } as VolumeConfigInterface["bundles_design"],
                    general_settings: {
                        ...ps.general_settings,
                        // eslint-disable-next-line no-prototype-builtins
                        ...(globalSettings?.hasOwnProperty("general_settings")
                            ? {
                                  ...globalSettings?.general_settings,
                                  show_powered_by_kefi: globalSettings?.general_settings?.hasOwnProperty(
                                      "show_powered_by_kefi",
                                  )
                                      ? globalSettings?.general_settings?.show_powered_by_kefi
                                      : true,
                              }
                            : {}),
                    },
                    bundles_settings: {
                        ...ps.bundles_settings,
                        ...globalSettings?.bundle_settings_fbt,
                    } as VolumeConfigInterface["bundles_settings"],
                }));
            }
        }
    }, [globalSettings, loading, bId]);

    useEffect(() => {
        if (bundleConfig?.form_id) {
            goTo(getBundleRoute(BundleTypes.FBT, bundleConfig?.form_id));
        }
    }, [bundleConfig?.form_id]);

    const fetchBundle = async (id: string | null = "") => {
        if (!id) {
            shopify.saveBar.hide("save-bar");
            goTo(getBundleRoute(BundleTypes.FBT));
        }
        setFetchingBundle(true);
        const res = await getData({ url: endPoints.getBundle + id });
        if (res.status == 200) {
            if (res?.data?.bundle?.form_id) {
                const newConfig = await updateBundleConfigWithRealTimeProducts(sft, [res.data.bundle]);
                const _c = {
                    ...bundleConfig,
                    ...res.data.bundle,
                    products: newConfig[0].products,
                    // @ts-expect-error required
                    offered_products: newConfig[0].offered_products,
                    publish_count: res.data.publish_count,
                    selected_template_fbt: res.data.bundle?.selected_template_fbt || "horizontal",
                    new_bundle_position:
                        res?.data?.bundle?.new_bundle_position ||
                        res.data.bundle?.bundles_settings?.bundle_position ||
                        "fbt_default",
                    new_custom_selector:
                        res?.data?.bundle?.new_custom_selector ||
                        res.data.bundle?.bundles_settings?.custom_selector ||
                        "",
                    discount_label_template: res.data.bundle.discount_label_template ?? res.data.bundle.title,
                    // Preserve manual_product_scope from saved bundle, default to "specific"
                    manual_product_scope: res.data.bundle?.manual_product_scope || "specific",
                };
                _c.bundles_design = {
                    ...bundleConfig?.bundles_design,
                    ...(userSaved ? _c.bundles_design : mergeDesignWithThemeColors(_c.bundles_design)),
                };
                _c.general_settings = {
                    ...bundleConfig?.general_settings,
                    ..._c.general_settings,
                    show_powered_by_kefi: _c?.general_settings?.hasOwnProperty("show_powered_by_kefi")
                        ? _c?.general_settings?.show_powered_by_kefi
                        : true,
                };
                _c.targeting = { ..._c.targeting, ...getUpdatedTargeting(_c.targeting) };
                setShowBundlerLevel(_c?.bundle_product_level == "variant" ? true : false);
                dispatchBundleConfig({
                    type: ChangeEvents.initState,
                    init: {
                        ..._c,
                    },
                });
                setConfig({
                    ..._c,
                });
                setFetchingBundle(false);
                reset();
            } else {
                shopify.saveBar.hide("save-bar");
                goTo(getBundleRoute(BundleTypes.FBT));
            }
        } else {
            shopify.saveBar.hide("save-bar");
            goTo(getBundleRoute(BundleTypes.FBT));
        }
    };

    // console._log("bundle id", bId, setIsMobile, maxLength);
    console._log("---_config------", _config, bundleConfig);

    const handleNext = async (num: number, screen = "-") => {
        submit();
        if (!validate().length) {
            trackFromTabs({
                cta: "Form Tabs Switched",
                tabName: screen,
                bundleType: bundleConfig?.bundle_type,
            });
            setSelected(num);
        } else {
            scrollToTop();
        }
    };

    // --- Inspector mode -----
    const [inspectorMode, setInspectorMode] = useState(false);
    const hoveredElRef = useRef<HTMLElement | null>(null);
    const [selectedEl, setSelectedEl] = useState<HTMLElement | null>(null);
    const [inspectorTarget, setInspectorTarget] = useState<any>(null);
    const previewRef = useRef<HTMLDivElement>(null);

    const { handleInspectorClick } = useInspectorMode({
        inspectorMode,
        previewRef,
        selectedEl,
        setSelectedEl,
        hoveredElRef,
        setInspectorTarget,
        handleNext,
        bundleConfig,
    });

    return (
        <FBTBundleContext.Provider
            value={{
                bundleConfig,
                dispatchBundleConfig,
                handleNext,
                fields,
                submit,
                reset,
                makeClean,
                validate,
                isMobile,
                SwatchesBundleConfigTouchedRef,
                // --- inspector mode additions ---
                inspectorMode,
                setInspectorMode,
                inspectorTarget,
            }}
        >
            {loading || fetchingBundle ? (
                <FormPreviewWrapper
                    oldBundleConfig={_config}
                    resetForm={reset}
                    bundleType={BundleTypes.FBT}
                    renderForms={[
                        <FBTContentForm showBundlerLevel={showBundlerLevel} />,
                        <FBTDesignForm />,
                        <FBTTargetForm />,
                        <FBTSettingsForm />,
                    ]}
                    renderPreview={
                        <div
                            ref={previewRef}
                            className={`${styles.previewContainer} ${inspectorMode ? styles.inspectorActive : ""}`}
                            style={{
                                userSelect: inspectorMode ? "none" : "text",
                                overflow: "visible",
                                padding: "22px 2px 2px 2px",
                            }}
                            onClickCapture={handleInspectorClick}
                        >
                            <FBTPreview
                                appConfigContext={appConfigContext}
                                bundleConfig={{
                                    ...bundleConfig,
                                    SwatchesBundleConfigTouchedRef: SwatchesBundleConfigTouchedRef,
                                }}
                                previewType={1}
                                inspectorMode={inspectorMode}
                            />
                        </div>
                    }
                    globalErrors={submitErrors}
                    submitForm={submit}
                    globalDirty={dirty}
                    dispatchBundleConfig={dispatchBundleConfig}
                    selected={selected}
                    setSelected={setSelected}
                    handleNext={handleNext}
                    bundleConfig={bundleConfig}
                    setConfig={setConfig}
                    validateForm={validate}
                    fetchBundle={fetchBundle}
                    loading={fetchingBundle || loading}
                />
            ) : bundleConfig?.selected_template_fbt || bundleConfig?.form_id ? (
                <FormPreviewWrapper
                    oldBundleConfig={_config}
                    resetForm={reset}
                    bundleType={BundleTypes.FBT}
                    renderForms={[
                        <FBTContentForm showBundlerLevel={showBundlerLevel} />,
                        <FBTDesignForm />,
                        <FBTTargetForm />,
                        <FBTSettingsForm />,
                    ]}
                    renderPreview={
                        <div
                            ref={previewRef}
                            className={`${styles.previewContainer} ${inspectorMode ? styles.inspectorActive : ""}`}
                            style={{
                                userSelect: inspectorMode ? "none" : "text",
                                overflow: "visible",
                                padding: "22px 2px 2px 2px",
                            }}
                            onClickCapture={handleInspectorClick}
                        >
                            <FBTPreview
                                appConfigContext={appConfigContext}
                                bundleConfig={{
                                    ...bundleConfig,
                                    SwatchesBundleConfigTouchedRef: SwatchesBundleConfigTouchedRef,
                                }}
                                previewType={1}
                            />
                        </div>
                    }
                    globalErrors={submitErrors}
                    submitForm={submit}
                    globalDirty={dirty}
                    dispatchBundleConfig={dispatchBundleConfig}
                    selected={selected}
                    setSelected={setSelected}
                    handleNext={handleNext}
                    bundleConfig={bundleConfig}
                    setConfig={setConfig}
                    validateForm={validate}
                    fetchBundle={fetchBundle}
                    loading={fetchingBundle || loading}
                    inspectorMode={inspectorMode}
                    setInspectorMode={setInspectorMode}
                />
            ) : (
                <TemplateSelectionScreen
                    bundleConfig={bundleConfig}
                    dispatchBundleConfig={dispatchBundleConfig}
                    makeClean={makeClean}
                />
            )}
        </FBTBundleContext.Provider>
    );
};

export default FBT;
